import URLSearchParams from '@ungap/url-search-params';
import { Col, Container, Row } from '@zoom/components';
import classnames from 'classnames';
import { addDays, isValid, startOfWeek, subDays } from 'date-fns';
import { TFunction } from 'i18next';
import { pathOr } from 'ramda';
import React, { useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';

import { CalendarExportButton } from './CalendarExport';
import { CalendarExportOld } from './CalendarExport/CalendarExportOldDesign';
import { useAuth } from '../../../app/AuthorizationProvider';
import { DatePickerValueType } from '../../../components/DatePicker/DatePicker';
import { PeriodStartButton } from '../../../components/DatePicker/PeriodStartButton';
import { WeekSwitcherBar } from '../../../components/DatePicker/WeekSwitcherBar';
import { MyScheduleTypeSwitcherOld } from '../../../components/MySchedule/MyScheduleTypeSwitcherOld';
import { MyScheduleWeeklyContainer } from '../../../components/MySchedule/MyScheduleWeeklyContainer';
import { Content, HeaderPropsType } from '../../../components/layout/Content';
import { Separator } from '../../../components/layout/Separator';
import { GetCalendarExportUrlComponent } from '../../../graphql/components';
import { CalendarExportURLResult } from '../../../graphql/types';
import { compose } from '../../../utils/compose';

const getHeaderProps = (
  t: TFunction,
  selectedDate: Date,
  handleDateChange: (value: DatePickerValueType) => void,
  isExportCalendarActive: boolean,
  setExportCalendarActive: (active: boolean) => void
): HeaderPropsType => ({
  actionBar: (
    <Row>
      <Col xs="auto">
        <MyScheduleTypeSwitcherOld date={selectedDate} t={t} />
      </Col>
      <Separator />
      <Col xs="auto d-lg-none">
        <CalendarExportButton active={isExportCalendarActive} onClick={setExportCalendarActive} />
      </Col>
      <Col xs="auto">
        <WeekSwitcherBar
          handleNextWeek={() => handleDateChange(addDays(selectedDate, 7))}
          handlePreviousWeek={() => handleDateChange(subDays(selectedDate, 7))}
          handleWeekChange={handleDateChange}
          pickedDate={selectedDate}
          t={t}
        />
      </Col>
      <Separator className="d-none d-lg-block" />
      <Col xs="auto">
        <PeriodStartButton handlePickedDate={handleDateChange} pickedDate={selectedDate} />
      </Col>
      <Col xs="auto" className="ml-auto d-none d-lg-block">
        <CalendarExportButton active={isExportCalendarActive} onClick={setExportCalendarActive} />
      </Col>
    </Row>
  ),
  title: t(`schedule.mySchedule.mySchedule`),
  testId: 'my-schedule:weekly-view:title',
  titleSlug: 'my-schedule',
});

interface SchedulingAgentsWeeklyViewProps extends RouteComponentProps, WithTranslation {}

const SchedulingAgentsWeeklyViewSceneComponent: React.FC<SchedulingAgentsWeeklyViewProps> = ({
  location,
  t,
  history,
}) => {
  const routedDate = pathOr(undefined, ['state', 'date'], location);
  const routedQuery = pathOr(undefined, ['search'], location);

  const [isExportCalendarActive, setExportCalendarActive] = useState(false);
  const auth = useAuth();

  let queryDate = null;

  if (routedQuery) {
    const queryParameters = new URLSearchParams(routedQuery);
    const dateQueryParam = queryParameters.get('date');

    if (dateQueryParam) {
      const parsedDate = new Date(dateQueryParam);

      if (parsedDate && isValid(parsedDate)) {
        queryDate = parsedDate;
      }

      history.replace({
        search: '',
      });
    }
  }

  const initialDate = routedDate || queryDate || new Date();

  const defaultDate = startOfWeek(initialDate, { locale: auth.localeObj });
  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [isDateHighlighted, setDateHighlighted] = useState(!!routedDate);

  const handleDateChange = (value: DatePickerValueType) => {
    if (value) {
      setSelectedDate(value);
    }

    if (isDateHighlighted) {
      setDateHighlighted(false);
    }
  };

  return (
    <Content
      header={getHeaderProps(t, selectedDate, handleDateChange, isExportCalendarActive, setExportCalendarActive)}
    >
      <Container
        className={classnames('my-schedule__container', 'weekly-view', 'container--wide-content')}
        data-test-id="my-schedule:weekly-view:container"
      >
        <GetCalendarExportUrlComponent variables={{ agentId: auth.userId }}>
          {({ data }: CalendarExportURLResult) =>
            isExportCalendarActive ? <CalendarExportOld url={data?.getCalendarExportURL?.url || ''} /> : null
          }
        </GetCalendarExportUrlComponent>
        <MyScheduleWeeklyContainer
          selectedDate={selectedDate}
          highlightedDate={isDateHighlighted ? routedDate : undefined}
        />
      </Container>
    </Content>
  );
};

export const SchedulingAgentsWeeklyViewScene = compose(
  withRouter,
  withTranslation()
)(SchedulingAgentsWeeklyViewSceneComponent);
